import { useState, useMemo, useContext } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  Typography,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TablePagination,
  Switch,
  alpha,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import StoreIcon from '@mui/icons-material/Store';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import axiosInstance from '../../lib/axios';
import { useSnackbar } from '../../components/SnackbarProvider';
import { TableToolbar, FilterOption, ColumnConfig } from '../../components/TableToolbar';
import { ResponsiveTable, Column } from '../../components/mobile/ResponsiveTable';
import type { Location, LocationType } from '../../types';
import { ColorModeContext } from '../../contexts/ColorModeContext';
import { palette, typography } from '../../theme/colors';
import { useAnimationStyles } from '../../hooks/useAnimations';

const fetchLocations = async () => {
  const response = await axiosInstance.get('/locations/admin/all');
  return Array.isArray(response.data) ? response.data : response.data.data || [];
};

const filterOptions: FilterOption[] = [
  {
    field: 'type',
    label: 'Type',
    type: 'select',
    options: [
      { value: 'store', label: 'Store' },
      { value: 'warehouse', label: 'Warehouse' },
    ],
  },
  {
    field: 'isActive',
    label: 'Status',
    type: 'select',
    options: [
      { value: 'true', label: 'Active' },
      { value: 'false', label: 'Inactive' },
    ],
  },
];

const exportColumns: ColumnConfig[] = [
  { field: 'name', headerName: 'Name' },
  { field: 'type', headerName: 'Type' },
  { field: 'city', headerName: 'City' },
  { field: 'wilaya', headerName: 'Wilaya' },
  { field: 'currency', headerName: 'Currency' },
  { field: 'phone', headerName: 'Phone' },
  { field: 'email', headerName: 'Email' },
  {
    field: 'isActive',
    headerName: 'Active',
    valueFormatter: (value) => (value ? 'Yes' : 'No'),
  },
];

const getTypeColor = (type: LocationType) => {
  switch (type) {
    case 'store':
      return palette.primary;
    case 'warehouse':
      return palette.accent;
    default:
      return palette.textMuted;
  }
};

export const LocationList = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();
  const { mode } = useContext(ColorModeContext);
  const animations = useAnimationStyles();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);

  const { data: locations = [], isLoading, error } = useQuery({
    queryKey: ['locations'],
    queryFn: fetchLocations,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => axiosInstance.delete(`/locations/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['locations'] });
      showMessage('Location deleted successfully', 'success');
      setDeleteDialogOpen(false);
    },
    onError: () => {
      showMessage('Failed to delete location', 'error');
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      axiosInstance.patch(`/locations/${id}`, { isActive }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['locations'] });
      showMessage('Location status updated', 'success');
    },
    onError: () => {
      showMessage('Failed to update location status', 'error');
    },
  });

  const filteredLocations = useMemo(() => {
    return locations.filter((location: Location) => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch =
          location.name.toLowerCase().includes(query) ||
          location.city?.toLowerCase().includes(query) ||
          location.email?.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }

      // Type filter
      if (filters.type && location.type !== filters.type) {
        return false;
      }

      // Active filter
      if (filters.isActive !== undefined && filters.isActive !== '') {
        const isActive = filters.isActive === 'true';
        if (location.isActive !== isActive) return false;
      }

      return true;
    });
  }, [locations, searchQuery, filters]);

  const paginatedLocations = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredLocations.slice(start, start + rowsPerPage);
  }, [filteredLocations, page, rowsPerPage]);

  const tableColumns: Column<Location>[] = [
    {
      field: 'name',
      headerName: 'Name',
      render: (value, row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {row.type === 'store' ? (
            <StoreIcon sx={{ color: palette.primary }} />
          ) : (
            <WarehouseIcon sx={{ color: palette.accent }} />
          )}
          <Typography variant="body2" fontWeight={500}>
            {value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'type',
      headerName: 'Type',
      render: (value, row) => (
        <Chip
          label={value}
          size="small"
          sx={{
            bgcolor: alpha(getTypeColor(row.type), 0.1),
            color: getTypeColor(row.type),
            textTransform: 'capitalize',
          }}
        />
      ),
    },
    {
      field: 'city',
      headerName: 'City',
      render: (value) => value || '-',
    },
    {
      field: 'capabilities',
      headerName: 'Capabilities',
      render: (value, row) => (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {row.canSellToCustomers && (
            <Chip label="Sales" size="small" variant="outlined" />
          )}
          {row.canFulfillOnlineOrders && (
            <Chip label="Online" size="small" variant="outlined" />
          )}
          {row.canSendTransfers && (
            <Chip label="Send" size="small" variant="outlined" />
          )}
          {row.canReceiveTransfers && (
            <Chip label="Receive" size="small" variant="outlined" />
          )}
        </Box>
      ),
    },
    {
      field: 'isActive',
      headerName: 'Active',
      render: (value, row) => (
        <Switch
          checked={row.isActive}
          onChange={() => handleToggleActive(row)}
          size="small"
        />
      ),
    },
  ];

  const handleDeleteClick = (location: Location) => {
    setSelectedLocation(location);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (selectedLocation) {
      deleteMutation.mutate(selectedLocation.id);
    }
  };

  const handleToggleActive = (location: Location) => {
    toggleActiveMutation.mutate({ id: location.id, isActive: !location.isActive });
  };

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">Error loading locations</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, ...animations.fadeIn }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: typography.fontWeight.semibold }}>
          Locations
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/app/locations/create')}
          sx={{
            bgcolor: palette.primary,
            '&:hover': { bgcolor: alpha(palette.primary, 0.9) },
          }}
        >
          Add Location
        </Button>
      </Box>

      <Card sx={{ bgcolor: mode === 'dark' ? palette.surface : palette.light.surface }}>
        <TableToolbar
          title="Locations"
          searchValue={searchQuery}
          onSearchChange={setSearchQuery}
          filters={filterOptions}
          activeFilters={filters}
          onFilterChange={(field, value) => setFilters(prev => ({ ...prev, [field]: value }))}
          onClearFilters={() => setFilters({})}
          columns={exportColumns}
          data={filteredLocations}
          exportFileName="locations"
        />

        <ResponsiveTable
          columns={tableColumns}
          rows={paginatedLocations}
          loading={isLoading}
          emptyMessage="No locations found"
          primaryField="name"
          getPrimaryText={(row) => row.name || 'Unknown Location'}
          onRowClick={(location) => navigate(`/app/locations/${location.id}`)}
          onEdit={(location) => navigate(`/app/locations/${location.id}/edit`)}
          onDelete={(location) => handleDeleteClick(location)}
        />

        <TablePagination
          component="div"
          count={filteredLocations.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Location</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{selectedLocation?.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LocationList;
